import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { toPng } from "html-to-image";
import html2canvas from "html2canvas";
import styles from "./CertificateModal.module.css";
import { IoClose } from "react-icons/io5";
import DiscordIcon from "../../../../icon/DiscordIcon";
import XIcon from "../../../../icon/XIcon";
import BookMarkIcon from "../../../../icon/BookMarkIcon";
import DiscordAltIcon from "../../../../icon/DiscordAltIcon";
import { env } from "../../../../constants/env";
import axios from "axios";

// export const overlays = [
//   { id: "spartan", label: "Spartan", image: "/images/spartan-desktop.png" },
//   { id: "bull", label: "Bull", image: "/images/bull-desktop.png" },
//   { id: "chart", label: "Chart", image: "/images/chart-desktop.png" },
//   { id: "bear", label: "Bear", image: "/images/bear-desktop.png" },
// ];

const DesktopCertificate = forwardRef(
  (
    {
      data,
      selectedOverlay,
      setSelectedOverlay,
      downloading,
      setDownloading,
      overlays,
      onClose,
    },
    ref
  ) => {
    const [customText, setCustomText] = useState(
      data?.customText ||
        "Join the PropFunding Challenge free. Pass first, then pay."
    );

    const [leaderboard, setLeaderboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imagesReady, setImagesReady] = useState(false);

    const certificateRef = useRef(null);

    useImperativeHandle(ref, () => ({
      download() {
        downloadCertificate();
      },
    }));

    const waitForImages = async () => {
      const node = certificateRef.current;
      if (!node) return;

      const images = node.querySelectorAll("img");

      await Promise.all(
        Array.from(images).map(async (img) => {
          if (!img.complete || img.naturalWidth === 0) {
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });
          }

          // Force browser to fully decode image into memory
          if (img.decode) {
            await img.decode().catch(() => {});
          }
        })
      );
    };

    useEffect(() => {
      const fetchLeaderboard = async () => {
        try {
          const token = localStorage.getItem("userToken");

          const res = await axios.get(`${env.apiUrl}/api/leaderboard/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          setLeaderboard(res.data);
        } catch (err) {
          console.error("Failed to load leaderboard", err);
        } finally {
          setLoading(false);
        }
      };

      fetchLeaderboard();
    }, []);

    useEffect(() => {
      if (!loading && leaderboard && certificateRef.current) {
        waitForImages()
          .then(() => setImagesReady(true))
          .catch(() => setImagesReady(true)); // fail-safe
      }
    }, [loading, leaderboard]);

    useEffect(() => {
      if (!certificateRef.current) return;

      setImagesReady(false);

      waitForImages()
        .then(() => setImagesReady(true))
        .catch(() => setImagesReady(true));
    }, [selectedOverlay, leaderboard]);

    if (loading) {
      return (
        <div className={styles.backdrop}>
          <div className={styles.loader}>Preparing certificate…</div>
        </div>
      );
    }

    console.log("IMAGE READY STATUS:", imagesReady);

    const trader = leaderboard?.trader;
    const rank = leaderboard?.rank || "—";
    const total = leaderboard?.total || leaderboard?.fieldSize || "—";

    const status =
      leaderboard?.status === "FUNDED"
        ? "FUNDED"
        : leaderboard?.status === "FAILED"
        ? "FAILED"
        : leaderboard?.status === "YET TO TRADE"
        ? "YET TO TRADE"
        : "IN PROGRESS";

    const statusColor =
      status === "FAILED"
        ? "#FF8383"
        : status === "YET TO TRADE"
        ? "#B0B0B0"
        : status === "IN PROGRESS"
        ? "#FFB370"
        : "var(--primary-color)";

    // 🔥 DOWNLOAD CERTIFICATE AS IMAGE
    const downloadCertificate = async () => {
      if (!certificateRef.current || !leaderboard) return;

      try {
        setDownloading(true);

        const node = certificateRef.current;

        // 1️⃣ Wait for all images to be ready
        const imgs = node.querySelectorAll("img");
        await Promise.all(
          [...imgs].map((img) => {
            return new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) return resolve();
              img.crossOrigin = "anonymous";
              img.onload = resolve;
              img.onerror = resolve;
            });
          })
        );

        // 2️⃣ Render to canvas
        const canvas = await html2canvas(node, {
          backgroundColor: null,
          scale: 3,
          useCORS: true,
          allowTaint: false,
          logging: false,
          scrollX: 0,
          scrollY: -window.scrollY,
        });

        // 3️⃣ Convert to PNG
        const dataUrl = canvas.toDataURL("image/png", 1.0);

        // 4️⃣ Download file
        const filename = `propfunding-certificate-${trader}.png`;
        const link = document.createElement("a");
        link.download = filename;
        link.href = dataUrl;
        link.click();

        // 5️⃣ Notify backend that download happened
        const token = localStorage.getItem("userToken");

        await axios.post(
          `${env.apiUrl}/api/certificates/downloaded`,
          {
            trader: leaderboard.trader,
            userId: leaderboard.userId,
            rank: leaderboard.rank,
            total: leaderboard.total || leaderboard.fieldSize,
            status: leaderboard.status,
            overlay: selectedOverlay,
            filename,
            cohort: `${leaderboard.month} - ${leaderboard.year}`,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } catch (err) {
        console.error("Certificate download failed", err);
      } finally {
        setDownloading(false);
      }
    };

    return (
      <div className={styles.backdrop}>
        <div className={`container-fluid ${styles.modalContainer}`}>
          {/* CLOSE */}
          <button className={styles.closeBtn} onClick={onClose}>
            <IoClose size={22} />
          </button>

          <div className="row h-100">
            {/* LEFT — CERTIFICATE */}
            <div className="col-lg-8 d-flex align-items-center justify-content-center">
              {!imagesReady && (
                <div className={styles.imageLoader}>
                  <div class="spinner-border" role="status" />
                </div>
              )}
              <div
                ref={certificateRef}
                className={`${styles.certificate} ${styles.certificateDesktop}`}
                style={{
                  opacity: imagesReady ? 1 : 0,
                  transition: "opacity 0.2s ease",
                }}
              >
                <h6 className={styles.title}>
                  PROPFUNDING.COM CHALLENGE CERTIFICATE
                </h6>

                <div className="row mt-5">
                  <div className="col-6">
                    <p className={styles.label}>TRADER</p>
                    <h4 className={styles.value}>{trader}</h4>

                    <p className={`${styles.label} mt-5`}>RANK</p>
                    <div className={styles.rank}>
                      <span>#{rank}</span>
                      <h4 className={styles.value}>out of {total}</h4>
                    </div>

                    <p className={`${styles.label} mt-5`}>FUNDED STATUS</p>
                    <h2
                      className={styles.passed}
                      style={{ color: statusColor }}
                    >
                      {status}
                    </h2>

                    <p className={`${styles.tagline} mt-5`}>
                      Pass First, Then Pay!
                    </p>
                    <p className={styles.subText}>
                      Pass the challenge, pay the activation fee, keep up to 80%
                      of profits.
                    </p>
                  </div>

                  {/* OVERLAY PREVIEW */}
                  <div
                    className={`col-6 d-flex align-items-center justify-content-center ${styles.overlayPreview}`}
                  >
                    <img
                      src={
                        overlays.find((o) => o.id === selectedOverlay)?.image
                      }
                      alt="Overlay preview"
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — CONTROLS */}
            <div className="col-lg-4">
              <div
                className={styles.sidebar}
                style={{
                  opacity: imagesReady ? 1 : 0,
                  transition: "opacity 0.2s ease",
                }}
              >
                {/* <h6 className={styles.sectionTitle}>Customize your text</h6>
              <textarea
                className={styles.textarea}
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
              /> */}
                <h6 className={`${styles.sectionTitle} mt-4`}>Overlay</h6>
                <div className="row g-3">
                  {overlays.map((overlay) => (
                    <div className="col-6" key={overlay.id}>
                      <div
                        className={`${styles.overlayOption} ${
                          selectedOverlay === overlay.id ? styles.active : ""
                        }`}
                        onClick={() => setSelectedOverlay(overlay.id)}
                      >
                        <img
                          src={overlay.image}
                          alt={overlay.label}
                          className={styles.overlayThumb}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 d-grid gap-2">
                  <button
                    className={styles.outlineBtn}
                    onClick={downloadCertificate}
                    disabled={!imagesReady || downloading}
                  >
                    {downloading ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      />
                    ) : (
                      <BookMarkIcon />
                    )}
                    <span>{downloading ? "Downloading…" : "Save Image"}</span>
                  </button>

                  {/*
                <button className={styles.outlineBtn}>
                  <DiscordAltIcon color="var(--primary-color)" size={20} />
                  Share to Discord
                </button>

                <button className={styles.outlineBtn}>
                  <XIcon color="var(--primary-color)" size={20} />
                  Share on X
                </button>
                */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default DesktopCertificate;
